<script setup>
import { computed, ref, useAttrs } from "vue";
import { getButtonColor } from "@/colors.js";
import Icon from "@/components/Icon.vue";
import Spinner from "@/components/Spinner.vue";
// Teleported tooltip — vue3-popper rendered inline, where the orders table's
// overflow container clipped it and the pinned actions column painted over it.
// Aliased to `Popper` so the existing <Popper> tags are unchanged.
import Popper from "@/components/Tooltip.vue";
import { mdiSpeedometer } from "@mdi/js";

const props = defineProps({
  label: {
    type: [String, Number],
    default: null,
  },
  icon: {
    type: String,
    default: null,
  },
  /*
   * Trailing icon, rendered after the label. The landing site puts directional
   * icons on the right (`ArrowRightIcon ... ml-2`) and descriptive ones on the
   * left (`ListBulletIcon ... mr-2`) — this makes that distinction possible
   * here too, instead of forcing every icon in front of the label.
   */
  iconRight: {
    type: String,
    default: null,
  },
  fontawesome: {
    type: Array,
    default: null
  },
  href: {
    type: String,
    default: null,
  },
  target: {
    type: String,
    default: null,
  },
  to: {
    type: [String, Object],
    default: null,
  },
  type: {
    type: String,
    default: null,
  },
  /*
   * The eight keys every map in colors.js defines. This prop used to accept any
   * String, and `getButtonColor` resolves an unknown one to `undefined` — which
   * renders a button with no background, border or text colour at all, silently
   * and only on whichever branch produced the bad value.
   *
   * Nothing is broken today (every literal call site passes a valid key), so
   * this is a guard for the dynamic ones — `:color="channel.color"`,
   * `:color="confirmDialog.color"` and friends, where the value arrives from
   * data at runtime and no reviewer can eyeball it. Vue runs `validator` in dev
   * only, so it costs nothing in production.
   */
  color: {
    type: String,
    default: "white",
    validator: (value) =>
      ["white", "light", "success", "danger", "warning", "info", "brand", "accent"].includes(value),
  },
  as: {
    type: String,
    default: null,
  },
  spinner: {
    type: Boolean,
    default: false,
  },
  showActive: {
    type: Boolean,
    default: null,
  },
  small: Boolean,
  outline: Boolean,
  active: Boolean,
  disabled: Boolean,
  popperContent: {
    type: String,
    default: null,
  },
});

const attrs = useAttrs();

const is = computed(() => {
  if (props.as) {
    return props.as;
  }

  if (props.to) {
    return "router-link";
  }

  if (props.href) {
    return "a";
  }

  return "button";
});

const computedType = computed(() => {
  if (is.value === "button") {
    return props.type ?? "button";
  }

  return null;
});

/*
 * Outbound links must never disclose this app. `noreferrer` strips the Referer
 * header (and implies `noopener`); `referrerpolicy` is the belt to that braces
 * in case the rel is ever overridden by a call site. The document-level
 * <meta name="referrer" content="no-referrer"> in index.html is the primary
 * guarantee — this makes the intent explicit and survives that meta changing.
 * A caller-supplied `rel` wins, so an intentional exception is still possible.
 */
const computedRel = computed(() => {
  if (is.value !== "a") return null;
  return attrs.rel ?? "noopener noreferrer";
});

const computedReferrerPolicy = computed(() => {
  if (is.value !== "a") return null;
  return attrs.referrerpolicy ?? "no-referrer";
});

/*
 * Only what the rendered element understands. These used to be bound to every
 * branch, and for RouterLink they arrive as fallthrough attrs, which override
 * the component's own: our `href` (null) replaced the href RouterLink computes
 * from `to`, so every <jb-button to="…"> was an <a> with NO href — skipped by
 * Tab, no "open in new tab" — carrying a meaningless disabled="false".
 */
const elementAttrs = computed(() => {
  const busy = props.spinner ? { "aria-busy": "true" } : {};

  if (is.value === "router-link") {
    return {
      to: props.to,
      ...(props.target ? { target: props.target } : {}),
      ...(props.disabled ? { "aria-disabled": "true", tabindex: -1 } : {}),
      ...busy,
    };
  }

  return {
    href: props.href,
    type: computedType.value,
    to: props.to,
    target: props.target,
    rel: computedRel.value,
    referrerpolicy: computedReferrerPolicy.value,
    // A real attribute only on a <button>; on an <a> it rendered "false".
    disabled: is.value === "button" ? props.disabled : null,
    "aria-disabled": is.value !== "button" && props.disabled ? "true" : null,
    ...busy,
  };
});

// Icon and label are spaced with a flex gap now, so the label needs no padding
// of its own; keep the computed for call sites that still read it.
const labelClass = computed(() => (props.small ? "leading-tight" : ""));

// solid colored buttons get a white wave; white/light/outline buttons keep blue
const spinnerColor = computed(() =>
  !props.outline && !["white", "light"].includes(props.color) ? "white" : "blue"
);

const componentClass = computed(() => {
  const base = [
    "inline-flex",
    "justify-center",
    "items-center",
    "whitespace-nowrap",
    // Matches the landing site's `.btn`: rounded-lg, font-medium, px-5 py-2.5.
    "font-medium",
    "focus:outline-none",
    "focus-visible:ring-2",
    "focus-visible:ring-offset-2",
    "dark:focus-visible:ring-offset-gray-900",
    // Colours only: hover changes the fill, border and text (colorsBgHover),
    // and nothing moves, not even on press.
    "transition-colors",
    "border",
    "rounded-lg",
    props.small ? "text-xs px-3 py-1.5" : "text-sm px-5 py-2.5",
    props.active ? "ring-2 ring-brand-500 ring-offset-2 dark:ring-offset-gray-900" : "",
    getButtonColor(props.color, props.outline, !props.disabled),
  ];

  if (props.disabled) {
    base.push("cursor-not-allowed", "opacity-50", "pointer-events-none");
  } else {
    base.push("cursor-pointer");
  }

  return base;
});
</script>

<template>
  <component :is="is" :class="componentClass" v-bind="elementAttrs">

    <Popper v-if="props.popperContent" :hover="true" :content="props.popperContent">
      <span class="flex flex-row items-center justify-center gap-2">
        <spinner v-if="spinner" :displayed="true" :color="spinnerColor" />
        <icon v-else-if="props.showActive" :path="mdiSpeedometer" :size="16" />
        <icon v-else-if="icon" :path="icon" :size="small ? 15 : 18" w="w-5" h="h-5" />
        <font-awesome-icon v-if="props.fontawesome && !spinner" :icon="props.fontawesome" />
        <span v-if="label && !spinner" :class="labelClass">{{ label }}</span>
        <!-- The spinner replaces the label visually; keep it for screen readers
             so a busy button still has a name. -->
        <span v-else-if="label" class="sr-only">{{ label }}</span>
        <icon v-if="iconRight && !spinner" :path="iconRight" :size="small ? 15 : 18" w="w-5" h="h-5" />
      </span>
    </Popper>

    <!-- Without a tooltip we skip the Popper wrapper entirely: it renders an
         inline-block span that stops `w-full` buttons from centring. -->
    <span v-else class="flex flex-row items-center justify-center gap-2">
      <spinner v-if="spinner" :displayed="true" :color="spinnerColor" />
      <icon v-else-if="props.showActive" :path="mdiSpeedometer" :size="16" />
      <icon v-else-if="icon" :path="icon" :size="small ? 15 : 18" w="w-5" h="h-5" />
      <font-awesome-icon v-if="props.fontawesome && !spinner" :icon="props.fontawesome" />
      <span v-if="label && !spinner" :class="labelClass">{{ label }}</span>
      <span v-else-if="label" class="sr-only">{{ label }}</span>
      <icon v-if="iconRight && !spinner" :path="iconRight" :size="small ? 15 : 18" w="w-5" h="h-5" />
    </span>
  </component>
</template>
